import React from 'react';
import { Control, Controller } from 'react-hook-form';
import {
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	FormControl,
	SelectChangeEvent,
	Autocomplete,
	TextField
} from '@mui/material';

export const FormSelect = ({
	name,
	control,
	label,
	rules,
	helperText,
	items,
	autocomplete,
	size,
	disabled,
	onChange,
	multiple = false,
	menuProps,
	itemProps,
	emptyFallback,
	noFW = false,
	width = '100%',
	dataTestId,
	freeSolo = true,
	disablePortal = true
}: {
	name: string;
	control: Control<any, any>;
	label: string;
	rules?: Record<string, unknown>;
	helperText?: string;
	items: { value?: string; label?: string; disabled?: any; accessBy?: string[] }[];
	autocomplete?: boolean;
	disabled?: boolean;
	size?: {
		selectSize: 'small' | 'medium';
		labelSize: 'small' | 'normal';
	};
	onChange?: (event?: SelectChangeEvent | React.SyntheticEvent, child?: React.ReactNode) => void;
	multiple?: boolean;
	menuProps?: any;
	itemProps?: any;
	emptyFallback?: any;
	noFW?: boolean;
	width?: string;
	dataTestId?: string;
	freeSolo?: boolean;
	disablePortal?: boolean;
}) => {
	return (
		<Controller
			name={name}
			control={control}
			rules={rules}
			render={({ field: { onChange: onControllerChange, value }, fieldState: { error } }) => (
				<FormControl fullWidth={!noFW} error={!!error} sx={{ width: width }}>
					{autocomplete ? (
						<Autocomplete
							id="autocomplete"
							disablePortal={disablePortal}
							options={items}
							value={value}
							fullWidth
							freeSolo={freeSolo}
							getOptionLabel={(option) =>
								option.label ?? items.find((option) => option?.value === value)?.label ?? option
							}
							isOptionEqualToValue={(option, value) => {
								return option?.value === value;
							}}
							disabled={disabled}
							renderInput={(params) => (
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
									error={!!error}
									InputProps={{ ...params.InputProps, autoComplete: 'new-password' }}
									inputProps={{
										...params.inputProps,
										'data-testid': dataTestId
									}}
								/>
							)}
							onChange={(e, c) => {
								if (!c) {
									onControllerChange(e);
									if (onChange) {
										onChange(e, '');
									}
									return;
								}
								// update event to get only value in the target
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								e.target = { ...e.target, value: c?.value ?? '' };
								onControllerChange(e);
								if (onChange) {
									onChange(e, c?.value ?? '');
								}
							}}
						/>
					) : (
						<>
							<InputLabel
								id={`select-label-${name}`}
								size={
									size?.labelSize === 'small' || size?.labelSize === 'normal'
										? size?.labelSize
										: 'small'
								}
							>
								{label}
							</InputLabel>
							<Select
								id="simple-select"
								multiple={multiple}
								labelId={`select-label-${name}`}
								variant="outlined"
								value={value}
								label={label}
								disabled={disabled}
								onChange={(e, c) => {
									onControllerChange(e);
									if (onChange) {
										onChange(e, c);
									}
								}}
								size={
									size?.selectSize === 'small' || size?.selectSize === 'medium'
										? size?.selectSize
										: 'small'
								}
								fullWidth
								MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
								inputProps={{
									'data-testid': dataTestId
								}}
							>
								{menuProps}
								{items.map(({ value, label, disabled }) => (
									<MenuItem key={value} value={value} {...itemProps} disabled={disabled?.disabled}>
										{label}
									</MenuItem>
								))}
								{items.length === 0 && emptyFallback ? emptyFallback : null}
							</Select>
						</>
					)}
					<FormHelperText>{error ? error.message : helperText ?? null}</FormHelperText>
				</FormControl>
			)}
		/>
	);
};
